import { SoftSkillCategory } from 'generated/graphql';
import { useSkillsFromProjectJobRoles } from 'hooks/useSkillsFromProjectJobRoles';
import { lowerCase } from 'lodash';
import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Loader,
  Tag,
  TagColor,
  TagSize,
} from '@spotted-zebra-uk/ui-components';
import styles from './StageSkillsProfile.module.scss';

const categorySkills = [
  lowerCase(SoftSkillCategory.RightForTheFuture),
  lowerCase(SoftSkillCategory.RightForUs),
  lowerCase(SoftSkillCategory.RightForTheRole),
];

export type SkillsProfileSkill = { id: number; name: string };

const MAX_PRIORITY_SKILLS = 6;

type SkillsProfileSectionProps = {
  softSkills: SkillsProfileSkill[];
  technicalSkills: SkillsProfileSkill[];
  onUpdateSoftSkills: (softSkill: SkillsProfileSkill) => void;
  onUpdateTechnicalSkills: (technicalSkill: SkillsProfileSkill) => void;
};
const SkillsProfileSection = ({
  softSkills,
  technicalSkills,
  onUpdateSoftSkills,
  onUpdateTechnicalSkills,
}: SkillsProfileSectionProps) => {
  const { projectId } = useParams();
  const { softSkillsByCategory, parsedTechnicalSkills, isLoading } =
    useSkillsFromProjectJobRoles({
      projectId: Number(projectId),
      fetchPolicy: 'cache-and-network',
      isMainRole: true,
    });

  const isSelectionDisabled = useMemo(
    () => softSkills.length + technicalSkills.length >= MAX_PRIORITY_SKILLS,
    [softSkills, technicalSkills],
  );

  if (isLoading) {
    return <Loader />;
  }

  return (
    <section className={styles.container}>
      <h3 className={styles.skillsProfileTitle}>
        Select up to 6 priority skills for this stage (
        {softSkills.length + technicalSkills.length}/{MAX_PRIORITY_SKILLS}{' '}
        selected)
      </h3>
      <div className={styles.categoryContainer}>
        <h3 className={styles.categoryTitle}>Right for the role</h3>
        <div className={styles.subCategoryContainer}>
          {softSkillsByCategory.rightForRoleSkills.length > 0 && (
            <h4 className={styles.subCategoryTitle}>Soft skills</h4>
          )}
          <SkillsProfileCategory
            skills={softSkillsByCategory.rightForRoleSkills}
            onUpdateSkills={onUpdateSoftSkills}
            selectedSkills={softSkills}
            disableSelection={isSelectionDisabled}
            label="soft skills"
          />
        </div>

        <div className={styles.subCategoryContainer}>
          {parsedTechnicalSkills.length > 0 && (
            <h4 className={styles.subCategoryTitle}>Technical skills</h4>
          )}
          <SkillsProfileCategory
            skills={parsedTechnicalSkills}
            onUpdateSkills={onUpdateTechnicalSkills}
            selectedSkills={technicalSkills}
            label="technical skills"
            subtitle="Technical skills"
            disableSelection={isSelectionDisabled}
          />
        </div>
      </div>

      <SkillsProfileCategory
        title="Right for us"
        skills={softSkillsByCategory.rightForUsSkills}
        onUpdateSkills={onUpdateSoftSkills}
        selectedSkills={softSkills}
        label="values"
        disableSelection={isSelectionDisabled}
      />
      <SkillsProfileCategory
        title="Right for the future"
        skills={softSkillsByCategory.rightForFuture}
        onUpdateSkills={onUpdateSoftSkills}
        selectedSkills={softSkills}
        label="indicators of potential"
        disableSelection={isSelectionDisabled}
      />
    </section>
  );
};

export default SkillsProfileSection;

const SkillsProfileCategory = ({
  title,
  subtitle,
  skills,
  onUpdateSkills,
  selectedSkills,
  label,
  disableSelection,
}: {
  title?: string;
  subtitle?: string;
  skills: SkillsProfileSkill[];
  onUpdateSkills: (skill: SkillsProfileSkill) => void;
  selectedSkills: SkillsProfileSkill[];
  label:
    | 'soft skills'
    | 'technical skills'
    | 'values'
    | 'indicators of potential';
  disableSelection?: boolean;
}) => {
  if (skills.length === 0) {
    return (
      <div className={styles.categoryContainer}>
        {title && <h3 className={styles.categoryTitle}>{title}</h3>}
        {subtitle && <p className={styles.subCategoryTitle}>{subtitle}</p>}
        <p className={styles.noSkills}>
          The added Role Skills Profile(s) don't include any {label}.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.categoryContainer}>
      {title && <h3 className={styles.categoryTitle}>{title}</h3>}
      <div className={styles.skillsContainer}>
        {skills
          // Filter out "Right for" framework soft skills
          .filter((skill) => !categorySkills.includes(skill.name.toLowerCase()))
          .map((skill) => {
            const isSelected = selectedSkills.some(
              (selectedSkill) => selectedSkill.id === skill.id,
            );
            return (
              <Tag
                key={skill.id}
                tagColor={isSelected ? TagColor.INTERACTIVE : TagColor.BLUE}
                size={TagSize.SMALL}
                isDismissible={false}
                ariaLabel={skill.name}
                onClick={() => onUpdateSkills(skill)}
                isDisabled={disableSelection && !isSelected}
              >
                {skill.name}
              </Tag>
            );
          })}
      </div>
    </div>
  );
};
